/**
 * Holds `BiChartsSettled` events that arrived before the client learned which
 * request they belong to, and releases them once it does.
 *
 * The race this closes: `runQuery` resets the accepted-id set and POSTs
 * `/query`; the server's `request_id` is only known when that HTTP response is
 * processed. But the worker can settle a chart and publish BEFORE then — an
 * ERROR settle needs no BigQuery at all, and the response itself is slow
 * enough to lose the race on a big dashboard (it compiles every chart before
 * it answers). An event landing in that window has a `turn_id` the client
 * cannot yet vouch for.
 *
 * Discarding it is PERMANENT, which is what makes this worth a module rather
 * than a shrug. The server has already recorded that chart in `published`, and
 * both re-emit paths compute `settled - published`: a later flush skips it and
 * so does the 90s `BiRequestSettleJob` backstop. `pendingKeys` is empty at that
 * instant too (cleared by the new generation, not yet seeded by the response),
 * so `reconcilePending` has nothing to re-read either. The tile hangs on
 * `loading` forever with no timeout path — the exact failure the accepted-id
 * check exists to prevent, arriving through the check itself.
 *
 * Holding is NOT the same as trusting: a held event is still only applied once
 * a response accepts its `turn_id`. One from a genuinely superseded request
 * never matches and ages out with its generation, so the supersession
 * guarantee (design doc section 6, "never paint a number computed under an old
 * filter state") is unchanged.
 *
 * Pure and separately tested, like `accepted-requests.ts` and for the same
 * reason: `bi-query.ts` keeps only the parts that genuinely cannot be.
 */

/** Runaway guard, not a tuning knob. A dashboard load produces a handful of
 *  settle events and holds them for milliseconds; anything approaching this
 *  means a request id is never arriving, in which case the OLDEST held events
 *  are the least likely to still matter. */
const MAX_HELD = 200;

export interface SettleBuffer<T> {
  /** Hold one event that could not be attributed yet, tagged with the
   *  generation that was current when it arrived. */
  hold(event: T, generation: number): void;
  /**
   * Return every held event of `generation` whose `turn_id` is now accepted,
   * in arrival order, and forget them. Events from any OTHER generation are
   * dropped (superseded — nothing will ever accept them). Events of this
   * generation that are still unattributed stay held: a second in-flight
   * request (a per-chart retry alongside the batch) may still claim them.
   */
  release(generation: number, isAccepted: (turnId: string) => boolean): T[];
  /** Forget everything — a new generation supersedes every held event. */
  clear(): void;
  /** Current size. For tests and debugging, not control flow. */
  readonly size: number;
}

export function createSettleBuffer<T extends { turn_id: string }>(): SettleBuffer<T> {
  let held: { event: T; generation: number }[] = [];
  return {
    hold(event: T, generation: number) {
      held.push({ event, generation });
      if (held.length > MAX_HELD) held = held.slice(held.length - MAX_HELD);
    },
    release(generation: number, isAccepted: (turnId: string) => boolean): T[] {
      if (held.length === 0) return [];
      const ready: T[] = [];
      const keep: { event: T; generation: number }[] = [];
      for (const entry of held) {
        if (entry.generation !== generation) continue;
        if (isAccepted(entry.event.turn_id)) ready.push(entry.event);
        else keep.push(entry);
      }
      held = keep;
      return ready;
    },
    clear() {
      held = [];
    },
    get size() {
      return held.length;
    },
  };
}
